// client/src/pages/TemplateFormPage.js
// client/src/pages/TemplateFormPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom'; // ADD 'Link' HERE
import { useAuth } from '../contexts/AuthContext';
import * as templatesApi from '../api/templates';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
function TemplateFormPage() {
  const { token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams(); // Get template ID from URL params (if editing)
  const isEditing = !!id; // Boolean to check if we are in edit mode

  const [formData, setFormData] = useState({
    name: '',
    type: '',
    description: '',
    items: [], // Array to hold template line items
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(true); // Loading state for initial fetch/submission

// Fetch template data if in edit mode
  useEffect(() => {
  const fetchTemplateData = async () => {
    if (authLoading) return; // Wait for auth to load

    if (isEditing) {
      if (!id) {
        setError('Invalid template ID. Unable to load template for editing.');
        setLoading(false);
        return;
      }
      try {
        const response = await templatesApi.fetchTemplateById(id);
        console.log('Fetched template response:', response); // Debug log
        
        const template = response.template;
        console.log('Template items after transform:', template.items); // Debug log
        
        setFormData({
          name: template.name || '',
          type: template.type || '',
          description: template.description || '',
          items: template.items || []
        });
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch template for editing:', err);
        setError(err.message || 'Failed to load template for editing.');
        setLoading(false);
      }
    } else {
      setLoading(false); // Not editing, so not loading data
    }
  };

  fetchTemplateData();
}, [id, isEditing, token, authLoading]);
  // Handle changes for main template fields
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Quill toolbar configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, false] }],
      ['bold', 'italic', 'underline'],
      [{'list': 'ordered'}, {'list': 'bullet'}],
      ['clean']
    ],
  };

  // Handle changes for individual template items
  // Handle changes for individual template items
  const handleItemChange = (itemId, field, value) => {
    setFormData(prevData => ({
      ...prevData,
      items: prevData.items.map(item =>
        item.id === itemId ? { ...item, [field]: value } : item
      ),
    }));
  };

  // Add a new item to the template
  const handleAddItem = () => {
    setFormData(prevData => {
      // Ensure unique temporary IDs for new items on the client side
      const newId = prevData.items.length > 0 ? Math.max(...prevData.items.map(item => typeof item.id === 'number' ? item.id : 0)) + 1 : 1;
      const newSortOrder = prevData.items.length > 0 ? Math.max(...prevData.items.map(item => item.sortOrder || 0)) + 1 : 0;
      return {
        ...prevData,
        items: [
          ...prevData.items,
          {
            id: newId, // Temporary client-side ID for new items
            itemName: '',
            unitType: 'unit',
            unitPrice: 0,
            description: '',
            isTaxable: true,
            sortOrder: newSortOrder,
          },
        ],
      };
    });
  };

  // Remove an item from the template
  const handleRemoveItem = (itemId) => {
    setFormData(prevData => ({
      ...prevData,
      items: prevData.items.filter(item => item.id !== itemId),
    }));
  };


  // Updated handleSubmit with better validation and error handling
const handleSubmit = async (e) => {
  e.preventDefault();
  setError(null);
  setSuccess(null);
  setLoading(true);

  // Validate form data before submission
  if (!formData.name.trim()) {
    setError('Template name is required.');
    setLoading(false);
    return;
  }

  if (formData.items.length === 0) {
    setError('Please add at least one item to the template.');
    setLoading(false);
    return;
  }

  // Validate each item
  for (let i = 0; i < formData.items.length; i++) {
    const item = formData.items[i];
    if (!item.itemName || !item.itemName.trim()) {
      setError(`Item ${i + 1}: Item name is required.`);
      setLoading(false);
      return;
    }
    if (!item.unitType) {
      setError(`Item ${i + 1}: Unit type is required.`);
      setLoading(false);
      return;
    }
    if (isNaN(item.unitPrice) || item.unitPrice < 0) {
      setError(`Item ${i + 1}: Unit price must be a valid number.`);
      setLoading(false);
      return;
    }
  }

  // Prepare data for submission
  const dataToSubmit = {
    name: formData.name.trim(),
    type: formData.type.trim(),
    description: formData.description.trim(),
    items: formData.items.map((item, index) => ({
      id: (typeof item.id === 'number' && item.id > 0) ? item.id : undefined, // Only send real IDs
      itemName: item.itemName.trim(),
      unitType: item.unitType,
      unitPrice: parseFloat(item.unitPrice || 0),
      description: (item.description || '').trim(),
      isTaxable: Boolean(item.isTaxable),
      sortOrder: item.sortOrder !== undefined ? item.sortOrder : index
    }))
  };

  console.log('Submitting template data:', dataToSubmit); // Debug log

  try {
    if (isEditing) {
      await templatesApi.updateTemplate(id, dataToSubmit);
      setSuccess('Template updated successfully!');
    } else {
      await templatesApi.createTemplate(dataToSubmit);
      setSuccess('Template created successfully!');
      navigate('/templates'); // Redirect to template list after creation
    }
  } catch (err) {
    console.error('Submission failed:', err);
    setError(err.message || 'Failed to save template. Please try again.');
  } finally {
    setLoading(false);
  }
};


  // Display loading state
  if (authLoading || loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-gray-50 p-4">
        <div className="text-xl text-gray-700">Loading Template Form...</div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto bg-white p-6 sm:p-8 rounded-xl shadow-lg border border-gray-200">
        <h2 className="text-3xl font-bold text-primary-700 mb-6 text-center">
          {isEditing ? 'Edit Template' : 'Create New Template'}
        </h2>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md relative mb-4" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md relative mb-4" role="alert">
            <span className="block sm:inline">{success}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Template Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Standard Bedroom Paint"
                className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                required
              />
            </div>
            <div>
              <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">Type (Optional)</label>
              <input
                type="text"
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                placeholder="e.g., Interior, Exterior"
                className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
          </div>
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
            <div className="mt-1 rounded-md shadow-sm border border-gray-300 focus-within:ring-1 focus-within:ring-primary-500 focus-within:border-primary-500 overflow-hidden w-full max-w-full">
              <ReactQuill
                theme="snow"
                value={formData.description}
                onChange={(value) => setFormData({ ...formData, description: value })}
                modules={quillModules}
              />
            </div>
          </div>

          {/* Template Items (Dynamic List - MOBILE OPTIMIZED) */}
          <div className="pt-6 border-t border-gray-200">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Template Items</h3>
            {formData.items.length === 0 ? (
              <p className="text-gray-500 text-center py-4 border border-dashed border-gray-300 rounded-lg mb-4">
                No items added to this template yet. Click "Add Item" below.
              </p>
            ) : (
              <div className="space-y-4 mb-4"> {/* Added mb-4 here to push the button down */}
                {/* Desktop Table View (hidden on small screens) */}
                <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-blue-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">Item Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">Unit Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">Unit Price</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">Taxable</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-blue-700 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {formData.items.map(item => (
                        <tr key={item.id} className="hover:bg-gray-50 transition duration-150 ease-in-out">
                          <td className="p-3">
                            <input
                              type="text"
                              value={item.itemName}
                              onChange={(e) => handleItemChange(item.id, 'itemName', e.target.value)}
                              className="block w-full p-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm mb-1"
                              placeholder="e.g., Walls, Trim"
                              required
                            />
                            <input
                              type="text"
                              value={item.description}
                              onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                              className="block w-full p-2 border border-gray-200 rounded-md text-xs text-gray-600"
                              placeholder="Description (Optional)"
                            />
                          </td>
                          <td className="p-3 whitespace-nowrap">
                            <select
                              value={item.unitType}
                              onChange={(e) => handleItemChange(item.id, 'unitType', e.target.value)}
                              className="block w-full p-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm"
                              required
                            >
                              <option value="sqft">sqft</option>
                              <option value="linear ft">linear ft</option>
                              <option value="each">each</option>
                              <option value="hours">hours</option>
                              <option value="unit">unit</option>
                            </select>
                          </td>
                          <td className="p-3 whitespace-nowrap">
                            <input
                              type="number"
                              value={item.unitPrice}
                              onChange={(e) => handleItemChange(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                              className="block w-24 p-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm text-right"
                              min="0"
                              step="0.01"
                              required
                            />
                          </td>
                          <td className="p-3 whitespace-nowrap text-center">
                            <input
                              type="checkbox"
                              checked={item.isTaxable}
                              onChange={(e) => handleItemChange(item.id, 'isTaxable', e.target.checked)}
                              className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                          </td>
                          <td className="p-3 whitespace-nowrap text-right">
                            <button
                              type="button"
                              onClick={() => handleRemoveItem(item.id)}
                              className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50 transition duration-150 ease-in-out"
                              aria-label="Remove item"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View (visible on small screens, hidden on md and up) */}
                <div className="md:hidden space-y-4">
                  {formData.items.map(item => (
                    <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm relative">
                      <h4 className="font-semibold text-gray-800 text-base mb-2">
                        <input
                          type="text"
                          value={item.itemName}
                          onChange={(e) => handleItemChange(item.id, 'itemName', e.target.value)}
                          className="block w-full p-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm font-medium mb-1"
                          placeholder="Item Name"
                          required
                        />
                      </h4>
                      <div className="text-sm text-gray-600 mb-3">
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                          className="block w-full p-2 border border-gray-200 rounded-md text-xs text-gray-600"
                          placeholder="Description (Optional)"
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                        <div>
                          <label htmlFor={`unitType-${item.id}`} className="block text-xs font-medium text-gray-500 mb-1">Unit Type</label>
                          <select
                            id={`unitType-${item.id}`}
                            value={item.unitType}
                            onChange={(e) => handleItemChange(item.id, 'unitType', e.target.value)}
                            className="block w-full p-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm"
                            required
                          >
                            <option value="sqft">sqft</option>
                            <option value="linear ft">linear ft</option>
                            <option value="each">each</option>
                            <option value="hours">hours</option>
                            <option value="unit">unit</option>
                          </select>
                        </div>
                        <div>
                          <label htmlFor={`unitPrice-${item.id}`} className="block text-xs font-medium text-gray-500 mb-1">Unit Price</label>
                          <input
                            type="number"
                            id={`unitPrice-${item.id}`}
                            value={item.unitPrice}
                            onChange={(e) => handleItemChange(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                            className="block w-full p-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm text-right"
                            min="0"
                            step="0.01"
                            required
                          />
                        </div>
                      </div>

                      <div className="flex justify-between items-center mb-3">
                        <label htmlFor={`isTaxable-${item.id}`} className="text-sm font-medium text-gray-700">Taxable</label>
                        <input
                          type="checkbox"
                          id={`isTaxable-${item.id}`}
                          checked={item.isTaxable}
                          onChange={(e) => handleItemChange(item.id, 'isTaxable', e.target.checked)}
                          className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                      </div>

                      <div className="text-right border-t border-gray-100 pt-3">
                         <button
                            type="button"
                            onClick={() => handleRemoveItem(item.id)}
                            className="text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-red-50 transition duration-150 ease-in-out"
                            aria-label="Remove item"
                          >
                            <svg className="w-5 h-5 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                            Remove Item
                          </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={handleAddItem}
              className="px-4 py-2 bg-gray-200 text-gray-800 font-medium rounded-lg shadow-sm hover:bg-gray-300 transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 focus:ring-opacity-75 text-sm mt-4" // This button now has mt-4
            >
              + Add Item
            </button>
          </div>

          {/* Form Submission Buttons */}
          <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-6 border-t border-gray-200">
            <Link
              to="/templates"
              className="w-full sm:w-auto text-center px-6 py-3 bg-gray-200 text-gray-800 font-medium rounded-lg shadow-md hover:bg-gray-300 transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 focus:ring-opacity-75"
            >
              Cancel
            </Link>
            <button
              type="submit"
              className="w-full sm:w-auto flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition duration-300"
              disabled={loading || authLoading}
            >
              {loading || authLoading ? (
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                isEditing ? 'Save Changes' : 'Create Template'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TemplateFormPage;
